import { Col, Row, Tooltip, Typography } from 'antd';
import { chunk, get, isEmpty, map, maxBy, some } from 'lodash-es';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import {
  EMPTY_VALUE,
  formatCodeValue,
  formatCodeValueShort,
  formatCodeValueWithFallback,
  formatFlightToLocationLong,
  formatLocationLong,
} from '../../../common/utils/formatUtils';
import {
  capitalizeWords,
  useNodeInnerText,
} from '../../../common/utils/stringUtils';
import Whitespace from '../../../components/Whitespace';
import { CurrencyFormat } from '../../../components/domainSpecific/currencyElements';
import { WeightFormat } from '../../../components/domainSpecific/measuringUnitsElements';
import { YesNoFormat } from '../../../components/forms/checkable';
import { SimpleCardSubtitle } from '../../../components/layout/cardElements';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import { BlockLabelWithValue, LabelRow } from '../../../components/typography';
import { useTypographyStyles } from '../../../components/typography.styles';
import { cssVariables } from '../../../styles/cssVariables';
import { pxToNumber } from '../../../utils/cssUtils';
import {
  DateFormatShortWithDashes,
  DateTimeFormatMedium,
} from '../../../utils/dateFormatting';
import {
  formatDateTimeSchedule,
  formatShipperSpecificInfoField,
} from '../shipmentFormats';
import {
  ShipmentSummaryCard,
  ShipmentSummaryCardSection,
  ShipmentSummaryDateTimeFormatFlight,
  ShipmentSummaryLabelWithValue,
  TwoLineLabelWithValue,
} from './shipmentSummariesCommon';
import { ShipmentChargesContent } from '../../../widgets/shipments/ShipmentChargesContent';
import { useTooltipStyles } from '../../../styles/tooltip.styles';
import { useShipmentOrderSummaryStyles } from './ShipmentOrderSummary.styles';
import { useShipmentSummariesCommonStyles } from './shipmentSummariesCommon.styles';

function getShipperSpecificInformationContent(shipment) {
  const shipperSpecificInfo = get(
    shipment,
    'serviceInformation.shipperSpecificInfo',
    []
  );
  if (!isEmpty(shipperSpecificInfo)) {
    const chunks = chunk(shipperSpecificInfo, 2);
    return chunks.map(([col1Info, col2Info], i) => (
      // eslint-disable-next-line react/no-array-index-key
      <LabelRow key={i} noBorder>
        <Col>
          <ShipmentSummaryLabelWithValue
            label={col1Info?.label}
            text={formatShipperSpecificInfoField(col1Info)}
          />
        </Col>
        <Col>
          {col2Info && (
            <ShipmentSummaryLabelWithValue
              label={col2Info.label}
              text={formatShipperSpecificInfoField(col2Info)}
            />
          )}
        </Col>
      </LabelRow>
    ));
  }
  return null;
}

function ShipperSpecificInformation({ shipment }) {
  const shipperSpecificInformation =
    getShipperSpecificInformationContent(shipment);
  return (
    shipperSpecificInformation && (
      <>
        <SimpleCardSubtitle titleId="shipmentDetail.shipperSpecificInformation" />
        <ShipmentSummaryCardSection>
          {shipperSpecificInformation}
        </ShipmentSummaryCardSection>
      </>
    )
  );
}

function AirlineFlightStatusCard({ shipment }) {
  const { styles } = useShipmentOrderSummaryStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { styles: typographyStyles } = useTypographyStyles();

  const baseLabelClass = classNames(
    typographyStyles.commonLabelValueLabel,
    styles.airlineLabel
  );
  const divLabelClass = classNames(baseLabelClass, styles.airlineLabelDiv);
  const baseTextClass = classNames(
    typographyStyles.commonLabelValueText,
    styles.airlineText
  );
  const mediumTextClass = classNames(baseTextClass, styles.airlineTextMedium);
  const smallTextClass = classNames(baseTextClass, styles.airlineTextSmall);
  const largeTextClass = classNames(baseTextClass, styles.airlineTextLarge);

  return (
    <ShipmentSummaryCard titleId="shipmentDetail.airlineFlightStatus">
      {shipment?.metadata?.blindingInformation?.flights ? (
        <div className={styles.blindedFlights}>
          <FormattedMessage id="gdpr.blinded" />
        </div>
      ) : (
        (shipment?.flights || []).map((flight, i) => {
          const estimateExists = some(
            [flight.departure, flight.arrival],
            sch => sch.estimated
          );
          const actualExists = some(
            [flight.departure, flight.arrival],
            sch => sch.actual
          );
          return (
            <Fragment key={get(flight, 'number')}>
              <div className={styles.airlineHeader}>
                <span className={baseLabelClass}>
                  <FormattedMessage id="shipmentDetail.awb" />:{' '}
                </span>
                <span className={baseTextClass}>{get(flight, 'awb')}</span>
              </div>
              <div className={styles.airlineSection}>
                <Row align="top" gutter={pxToNumber(cssVariables.spaceMd)}>
                  <Col>
                    <div className={styles.airlineIndex}>{i + 1}</div>
                  </Col>
                  <Col className={layoutStyles.flex1}>
                    <Row
                      justify="space-between"
                      style={{ marginBottom: cssVariables.spaceSm }}
                    >
                      <Col>
                        <Row gutter={pxToNumber(cssVariables.spaceXs)}>
                          <Col>
                            <div className={divLabelClass}>
                              <FormattedMessage id="shipmentDetail.airlineFlightStatus.airline" />{' '}
                              /
                            </div>
                            <div className={mediumTextClass}>
                              {get(flight, 'airline')}
                            </div>
                          </Col>
                          <Col>
                            <div className={divLabelClass}>
                              <FormattedMessage id="shipmentDetail.airlineFlightStatus.flight" />
                            </div>
                            <div className={mediumTextClass}>
                              {get(flight, 'number')}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col>
                        <div className={divLabelClass}>
                          <FormattedMessage id="shipmentDetail.airlineFlightStatus.origin" />
                        </div>
                        <div className={mediumTextClass}>
                          {get(flight, 'originAirportCode')}
                        </div>
                      </Col>
                      <Col>
                        <div className={divLabelClass}>
                          <FormattedMessage id="shipmentDetail.airlineFlightStatus.destination" />
                        </div>
                        <div className={mediumTextClass}>
                          {get(flight, 'destinationAirportCode')}
                        </div>
                      </Col>
                    </Row>
                    <Row
                      align="top"
                      gutter={pxToNumber(cssVariables.spaceSm)}
                      style={{ marginBottom: cssVariables.spaceXs }}
                    >
                      <Col className={divLabelClass}>
                        <FormattedMessage id="shipmentDetail.airlineFlightStatus.routeStatus" />
                        :
                      </Col>
                      <Col
                        className={classNames(largeTextClass, 'text-uppercase')}
                      >
                        {get(flight, 'status')}
                      </Col>
                    </Row>
                    <Row align="top">
                      <Col span={9}>
                        <div className={styles.indentLeft}>
                          <div className={divLabelClass}>
                            <FormattedMessage id="shipmentDetail.airlineFlightStatus.flightUpdates" />
                          </div>
                          <div className={smallTextClass}>
                            <FormattedMessage id="shipmentDetail.airlineFlightStatus.scheduled" />
                          </div>
                          {estimateExists && (
                            <div className={smallTextClass}>
                              <FormattedMessage id="shipmentDetail.airlineFlightStatus.estimated" />
                            </div>
                          )}
                          {actualExists && (
                            <div className={smallTextClass}>
                              <FormattedMessage id="shipmentDetail.airlineFlightStatus.actual" />
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className={divLabelClass}>
                          <FormattedMessage id="shipmentDetail.airlineFlightStatus.depart" />
                        </div>
                        <div className={smallTextClass}>
                          <ShipmentSummaryDateTimeFormatFlight
                            value={get(flight, 'departure.scheduled')}
                          />
                        </div>
                        {estimateExists && (
                          <div className={smallTextClass}>
                            <ShipmentSummaryDateTimeFormatFlight
                              value={get(flight, 'departure.estimated')}
                            />
                          </div>
                        )}
                        {actualExists && (
                          <div className={smallTextClass}>
                            <ShipmentSummaryDateTimeFormatFlight
                              value={get(flight, 'departure.actual')}
                            />
                          </div>
                        )}
                      </Col>
                      <Col span={7}>
                        <div className={divLabelClass}>
                          <FormattedMessage id="shipmentDetail.airlineFlightStatus.arrive" />
                        </div>
                        <div className={smallTextClass}>
                          <ShipmentSummaryDateTimeFormatFlight
                            value={get(flight, 'arrival.scheduled')}
                          />
                        </div>
                        {estimateExists && (
                          <div className={smallTextClass}>
                            <ShipmentSummaryDateTimeFormatFlight
                              value={get(flight, 'arrival.estimated')}
                            />
                          </div>
                        )}
                        {actualExists && (
                          <div className={smallTextClass}>
                            <ShipmentSummaryDateTimeFormatFlight
                              value={get(flight, 'arrival.actual')}
                            />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            </Fragment>
          );
        })
      )}
    </ShipmentSummaryCard>
  );
}

function ShipmentDangerousGoodsDeclaration({ shipment }) {
  const unNumbers = get(shipment, 'serviceInformation.unNumber');
  return (
    <ShipmentSummaryCard titleId="book.newShipment.dangerousGoodsDeclaration">
      <ShipmentSummaryCardSection className="spaces-vert-xs">
        <LabelRow>
          <Col>
            <ShipmentSummaryLabelWithValue
              labelSize="full"
              labelId="book.newShipment.label.dangerousGoods"
              text={
                <YesNoFormat
                  className="text-uppercase"
                  value={get(shipment, 'serviceInformation.dangerousGoods')}
                />
              }
            />
          </Col>
          <Col>
            <ShipmentSummaryLabelWithValue
              labelSize="full"
              labelId="book.newShipment.label.exceptedQuantity"
              text={
                <YesNoFormat
                  className="text-uppercase"
                  value={get(shipment, 'serviceInformation.exceptedQuantity')}
                />
              }
            />
          </Col>
        </LabelRow>
        {map(unNumbers, number => (
          <LabelRow>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelSize="full"
                labelId="book.newShipment.label.unNumber"
                text={formatCodeValueShort(number)}
              />
            </Col>
            <Col>
              <Tooltip title={formatCodeValue(number)}>
                <ShipmentSummaryLabelWithValue
                  labelSize="full"
                  labelId="book.newShipment.label.unDescription"
                  text={formatCodeValue(number)}
                />
              </Tooltip>
            </Col>
          </LabelRow>
        ))}
      </ShipmentSummaryCardSection>
    </ShipmentSummaryCard>
  );
}

const BlockLabelDateTimeFormat = props => (
  <DateTimeFormatMedium {...props} separator=" | " />
);
function BlockLabel({ labelId, dateLabelId, dateValue, ...rest }) {
  const { ref, text } = useNodeInnerText();
  const { styles } = useShipmentOrderSummaryStyles();
  return (
    <>
      <div className="font-size-base">
        <FormattedMessage id={labelId} />
      </div>
      <div>
        <span className={styles.dateLabel}>
          <FormattedMessage id={dateLabelId} />:<Whitespace />
        </span>
        <span ref={ref} className={styles.dateValue} title={text} {...rest}>
          {formatDateTimeSchedule(dateValue, BlockLabelDateTimeFormat)}
        </span>
      </div>
    </>
  );
}

function blindableText(text, { blinded, blindedClassName }) {
  if (!blinded) {
    return text;
  }
  return (
    <div className={blindedClassName}>
      <FormattedMessage id="gdpr.blinded" />
    </div>
  );
}

function ShipmentDetailsSpecialInstructions({ shipment }) {
  const { Paragraph } = Typography;
  const { styles: commonStyles } = useShipmentSummariesCommonStyles();
  const originSpecialInstructions = get(shipment, 'origin.specialInstructions');
  const formattedOriginSpecialInstructions = originSpecialInstructions
    ? capitalizeWords(originSpecialInstructions)
    : EMPTY_VALUE;
  const destinationSpecialInstructions = get(
    shipment,
    'destination.specialInstructions'
  );
  const formattedDestinationSpecialInstructions = destinationSpecialInstructions
    ? capitalizeWords(destinationSpecialInstructions)
    : EMPTY_VALUE;

  return (
    <ShipmentSummaryCardSection
      headerId="shipmentDetail.specialInstructions"
      collapsible
      className={commonStyles.sectionSpecialInstructions}
    >
      <LabelRow>
        <Col>
          <Paragraph
            ellipsis={{ rows: 3, expandable: false }}
            title={formattedOriginSpecialInstructions}
            className={commonStyles.specialInstructionsParagraph}
          >
            {formattedOriginSpecialInstructions}
          </Paragraph>
        </Col>
        <Col>
          <Paragraph
            ellipsis={{ rows: 3, expandable: false }}
            title={formattedDestinationSpecialInstructions}
            className={commonStyles.specialInstructionsParagraph}
          >
            {formattedDestinationSpecialInstructions}
          </Paragraph>
        </Col>
      </LabelRow>
    </ShipmentSummaryCardSection>
  );
}

function ShipmentChargesSection({ charges, currency, shipment }) {
  const { styles: commonStyles } = useShipmentSummariesCommonStyles();
  if (shipment?.metadata?.account?.partner || !charges?.items?.length) {
    return null;
  }

  return (
    <ShipmentSummaryCardSection
      headerId="shipmentDetail.charges"
      className={commonStyles.sectionCharges}
    >
      <ShipmentChargesContent
        charges={charges}
        currency={currency}
        rowClassName={commonStyles.chargesItem}
        nameColSpan={12}
        amountColSpan={12}
      />
    </ShipmentSummaryCardSection>
  );
}

export function ShipmentOrderSummary({ shipment, showAirlineFlightStatus }) {
  const isDelivered = shipment?.deliveryDateTime?.actual;
  const lastManualEdit = maxBy(shipment?.activity, e => e.dateTime?.valueOf());
  const { styles } = useTooltipStyles();
  const { styles: orderSummaryStyles } = useShipmentOrderSummaryStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();

  return (
    <div className="margins-bottom-norm2">
      <ShipmentSummaryCard titleId="shipmentDetail.orderDetails">
        <ShipmentSummaryCardSection>
          <LabelRow>
            <Col className={layoutStyles.flex1}>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.status"
                labelSize="full"
                text={get(shipment, 'status')}
                className={classNames({
                  [orderSummaryStyles.deliveredStatus]: isDelivered,
                })}
              />
            </Col>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.lastEdit"
                labelSize="full"
                text={
                  lastManualEdit ? (
                    <DateFormatShortWithDashes
                      value={lastManualEdit.dateTime}
                    />
                  ) : (
                    EMPTY_VALUE
                  )
                }
              />
            </Col>
          </LabelRow>
          <LabelRow noBorder>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.company"
                labelSize="full"
                text={get(shipment, 'metadata.account.name')}
              />
            </Col>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.account"
                labelSize="full"
                text={get(shipment, 'metadata.account.number')}
              />
            </Col>
          </LabelRow>
          {shipment?.metadata?.blindingInformation?.accountDetails ? (
            <LabelRow noBorder>
              <div className={orderSummaryStyles.blindedAccountDetails}>
                <FormattedMessage id="gdpr.blinded" />
              </div>
            </LabelRow>
          ) : (
            <LabelRow noBorder>
              <Col>
                <ShipmentSummaryLabelWithValue
                  labelId="shipmentDetail.contact"
                  labelSize="full"
                  text={get(shipment, 'authorizedBy.contact.name')}
                />
              </Col>
              <Col>
                <ShipmentSummaryLabelWithValue
                  labelId="shipmentDetail.phone"
                  labelSize="full"
                  text={get(shipment, 'authorizedBy.contact.phone')}
                />
              </Col>
            </LabelRow>
          )}
        </ShipmentSummaryCardSection>
        <ShipperSpecificInformation shipment={shipment} />
      </ShipmentSummaryCard>
      <ShipmentSummaryCard titleId="shipmentDetail.shipmentDetails">
        <ShipmentSummaryCardSection>
          <LabelRow>
            <Col>
              <BlockLabelWithValue
                label={
                  <BlockLabel
                    labelId="shipmentDetail.pickupLocation"
                    dateLabelId="job.readyDate.short"
                    dateValue={get(shipment, 'pickupDateTime')}
                    data-info-id="pickupDateTime"
                    data-format="short"
                  />
                }
                text={blindableText(
                  shipment?.isRetrieval
                    ? formatFlightToLocationLong(shipment?.retrievalFlight)
                    : formatLocationLong(shipment?.origin || {}),
                  {
                    blinded: shipment?.metadata?.blindingInformation?.origin,
                    blindedClassName: orderSummaryStyles.blindedAddress,
                  }
                )}
              />
            </Col>
            <Col>
              <BlockLabelWithValue
                label={
                  <BlockLabel
                    labelId="shipmentDetail.deliveryLocation"
                    dateLabelId="job.deadlineDate.short"
                    dateValue={get(shipment, 'deliveryDateTime')}
                    data-info-id="deliveryDateTime"
                    data-format="short"
                  />
                }
                text={blindableText(
                  formatLocationLong(shipment?.destination || {}),
                  {
                    blinded:
                      shipment?.metadata?.blindingInformation?.destination,
                    blindedClassName: orderSummaryStyles.blindedAddress,
                  }
                )}
              />
            </Col>
          </LabelRow>
          <LabelRow>
            <Col>
              <TwoLineLabelWithValue
                labelId="shipmentDetail.service"
                text={formatCodeValue(
                  get(shipment, 'serviceInformation.serviceType')
                )}
              />
            </Col>
            <Col>
              <TwoLineLabelWithValue
                labelId="shipmentDetail.comodity"
                text={get(shipment, 'serviceInformation.commodity')}
              />
            </Col>
          </LabelRow>
          <LabelRow>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.packages"
                text={get(shipment, 'packagesInformation.count')}
              />
            </Col>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.weight"
                text={
                  <WeightFormat
                    value={get(shipment, 'packagesInformation.totalWeight')}
                  />
                }
              />
            </Col>
          </LabelRow>
          <LabelRow>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.referenceNumber"
                labelSize="full"
                showNewlines
                text={get(shipment, 'shipperReference')}
              />
            </Col>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.bolNumber"
                labelSize="full"
                text={get(shipment, 'serviceInformation.bolNumber')}
              />
            </Col>
          </LabelRow>
          <LabelRow>
            <Col>
              <ShipmentSummaryLabelWithValue
                label={
                  <Tooltip
                    title={
                      <FormattedMessage id="shipmentDetail.eoriNumber.tooltip" />
                    }
                    classNames={{ root: styles.sm }}
                    placement="topLeft"
                  >
                    <FormattedMessage id="shipmentDetail.eoriNumber" />
                  </Tooltip>
                }
                text={get(shipment, 'serviceInformation.eoriNumber')}
              />
            </Col>
          </LabelRow>
          {shipment.serviceInformation?.incoterms && (
            <LabelRow>
              <Col>
                <ShipmentSummaryLabelWithValue
                  labelId="shipmentDetail.incoterms"
                  text={formatCodeValueWithFallback(
                    shipment.serviceInformation.incoterms
                  )}
                />
              </Col>
            </LabelRow>
          )}
          {shipment.serviceInformation?.hsInfo?.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <LabelRow key={i}>
              <Col>
                <ShipmentSummaryLabelWithValue
                  label={
                    <Tooltip
                      title={
                        <FormattedMessage id="shipmentDetail.hsCode.tooltip" />
                      }
                      classNames={{ root: styles.sm }}
                      placement="topLeft"
                    >
                      <FormattedMessage id="shipmentDetail.hsCode" />
                    </Tooltip>
                  }
                  labelSize="full"
                  text={item.code}
                />
              </Col>
              <Col>
                <ShipmentSummaryLabelWithValue
                  labelId="shipmentDetail.hsDescription"
                  labelSize="full"
                  text={item.description}
                />
              </Col>
            </LabelRow>
          ))}
          <LabelRow>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.additionalInsurance"
                labelSize="full"
                text={
                  <CurrencyFormat
                    currency={get(shipment, 'serviceInformation.currency.code')}
                    value={get(
                      shipment,
                      'serviceInformation.additionalInsurance'
                    )}
                  />
                }
              />
            </Col>
            <Col>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.declaredValue"
                labelSize="full"
                text={
                  <CurrencyFormat
                    currency={get(shipment, 'serviceInformation.currency.code')}
                    value={get(shipment, 'serviceInformation.declaredValue')}
                  />
                }
              />
            </Col>
            <Col span={24}>
              <ShipmentSummaryLabelWithValue
                labelId="shipmentDetail.currency"
                labelSize="full"
                text={get(shipment, 'serviceInformation.currency.text')}
              />
            </Col>
          </LabelRow>
        </ShipmentSummaryCardSection>
        <ShipmentDetailsSpecialInstructions shipment={shipment} />
        <ShipmentChargesSection
          charges={shipment?.charges}
          currency={get(shipment, 'serviceInformation.currency.code')}
          shipment={shipment}
        />
      </ShipmentSummaryCard>
      {showAirlineFlightStatus && (
        <AirlineFlightStatusCard shipment={shipment} />
      )}
      {typeof get(shipment, 'serviceInformation.dangerousGoods') ===
        'boolean' && <ShipmentDangerousGoodsDeclaration shipment={shipment} />}
    </div>
  );
}
